import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import api from '../api/index';
import { ENDPOINTS } from '../api/endpoints';

const faqId = (group, faq, idx) => `${group.category._id}-${faq._id ?? idx}`;

export default function Faq() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState(() => new Set());
  const [categoryId, setCategoryId] = useState('all');

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await api.get(ENDPOINTS.FAQ_GROUPED);
        if (response.data?.isOk && Array.isArray(response.data.data)) {
          setGroups(response.data.data.filter((g) => g.faqs?.length > 0));
        }
      } catch (err) {
        console.error('Failed to fetch FAQs', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  const visibleGroups = useMemo(() => {
    if (categoryId === 'all') return groups;
    return groups.filter((g) => g.category._id === categoryId);
  }, [groups, categoryId]);

  const visibleIds = useMemo(
    () =>
      visibleGroups.flatMap((group) =>
        group.faqs.map((faq, idx) => faqId(group, faq, idx))
      ),
    [visibleGroups]
  );

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenIds(new Set(visibleIds));
  const collapseAll = () => setOpenIds(new Set());

  const handleCategoryChange = (value) => {
    setCategoryId(value);
    setOpenIds(new Set());
  };

  return (
    <main className="pt-[10px] pb-16 bg-card min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-wide uppercase mb-3">
            Help Center
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
            Frequently Asked Questions
          </h1>
          <p className="mt-3 text-muted-foreground">
            Everything you need to know. Tap a question to reveal the answer.
          </p>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-14 bg-muted/60 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : groups.length > 0 ? (
          <>
            <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-border bg-background px-4 py-3 shadow-sm">
              <label className="flex items-center gap-2 min-w-0">
                <span className="sr-only">Filter by category</span>
                <select
                  value={categoryId}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full sm:w-auto min-w-[10rem] px-4 py-2.5 border-2 border-border rounded-lg text-sm bg-background text-foreground cursor-pointer outline-none hover:border-primary focus:border-primary transition-colors"
                >
                  <option value="all">All</option>
                  {groups.map((group) => (
                    <option key={group.category._id} value={group.category._id}>
                      {group.category.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={expandAll}
                  className="flex-1 sm:flex-none px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:brightness-110 transition-all whitespace-nowrap"
                >
                  Expand All
                </button>
                <button
                  type="button"
                  onClick={collapseAll}
                  className="flex-1 sm:flex-none px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:brightness-110 transition-all whitespace-nowrap"
                >
                  Collapse All
                </button>
              </div>
            </div>

            {visibleGroups.length > 0 ? (
              <div className="space-y-10">
                {visibleGroups.map((group) => (
                  <section key={group.category._id}>
                    {categoryId === 'all' && (
                      <h2 className="font-display text-xl font-bold text-foreground mb-4 pb-2 border-b border-border">
                        {group.category.name}
                      </h2>
                    )}
                    <div className="space-y-3">
                      {group.faqs.map((faq, idx) => {
                        const id = faqId(group, faq, idx);
                        const isOpen = openIds.has(id);
                        return (
                          <div
                            key={id}
                            className={`rounded-xl border bg-background overflow-hidden transition-colors ${
                              isOpen ? 'border-primary/60 shadow-sm' : 'border-border'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => toggle(id)}
                              aria-expanded={isOpen}
                              className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-muted/40 transition-colors"
                            >
                              <span className="font-medium text-foreground">{faq.question}</span>
                              <ChevronDown
                                className={`shrink-0 w-5 h-5 text-primary transition-transform duration-300 ${
                                  isOpen ? 'rotate-180' : ''
                                }`}
                              />
                            </button>
                            <div
                              className={`grid transition-all duration-300 ease-in-out ${
                                isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                              }`}
                            >
                              <div className="overflow-hidden">
                                <p className="px-5 pb-5 pt-0 text-muted-foreground leading-relaxed whitespace-pre-line">
                                  {faq.answer}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-muted-foreground">No FAQs found for this category.</p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <p className="text-muted-foreground">No FAQs available at the moment.</p>
          </div>
        )}
      </div>
    </main>
  );
}
